import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, of, Subject, takeUntil, tap } from 'rxjs';
import { ADMIN_PANEL_ALLOWED_ROLES } from '../../core/guards/auth/auth.guard';
import { clearStoredToken, getStoredTokenPayload } from '../../core/helpers/token/token.helper';
import { AuthService } from '../../core/services/auth/auth.service';

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);

  public loginForm: FormGroup<{
    email: FormControl<string>;
    password: FormControl<string>;
  }>;
  public loading = false;
  public error = '';
  public returnUrl = '';

  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();

  public ngOnInit(): void {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });

    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public getFormControl(name: keyof typeof this.loginForm.controls): FormControl<string> {
    return this.loginForm.controls[name] as FormControl<string>;
  }

  public onSubmit(): void {
    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;
    this.error = '';

    this.authService
      .login(this.getFormControl('email').value, this.getFormControl('password').value)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        tap((result) => {
          if (!result) {
            return;
          }

          // The token is already in localStorage by now — accessTokenHandlerInterceptor stores it upstream of
          // this tap. So a wrong role means revoking the session that was just created, not withholding it.
          const payload = getStoredTokenPayload();

          if (!payload || !ADMIN_PANEL_ALLOWED_ROLES.includes(payload.role)) {
            clearStoredToken();
            this.error = 'Цей акаунт не має прав адміністратора';

            return;
          }

          this.router.navigate([this.returnUrl]);
        }),
        catchError((error: HttpErrorResponse) => {
          this.error = error?.error?.message || 'Не вдалося увійти. Перевірте пошту та пароль';

          return of(false);
        }),
      )
      .subscribe(() => {
        this.loading = false;
      });
  }
}
